"use client";

import { useEffect, useState } from "react";
import { Check, Loader, Lock, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const deliverablesEn = ["Comprehensive manufacturing capability profile (PDF)", "Customized OEM/ODM engagement roadmap", "Quality assurance and compliance documentation", "Direct consultation with our production engineering team"];
const deliverablesZh = ["完整製造能力概覽文件 (PDF)", "客製化 OEM/ODM 合作規劃藍圖", "品質保證及合規檢驗文件", "與我們生產工程團隊直接諮詢洽談"];
function BriefingForm({
  isZh
}: {
  isZh: boolean;
}) {
  const {
    control,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-8 md:p-10 bg-background">
        <p className="font-default text-2xl text-foreground mb-4 leading-snug">
          {successMessage}
        </p>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          {isZh ? "我們的生產團隊將會評估您的具體需求，並於一個工作天內與您聯絡。所有項目細節均受嚴格保密協議保護。" : "Our production team will review your requirements and contact you within one business day. All project details are handled with strict confidentiality."}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-border hover:bg-accent">
          {isZh ? "提交另一個查詢" : "Submit another inquiry"}
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-7">
      <FormField control={control} name="organization" rules={{
      required: isZh ? "請輸入公司名稱" : "Company name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[0.75rem] font-mono font-medium tracking-[0.15em] uppercase text-foreground">
              {isZh ? "公司名稱" : "Company Name"}
            </FormLabel>
            <FormControl>
              <Input placeholder={isZh ? "例如：Global Toy Brands Inc." : "e.g., Global Toy Brands Inc."} {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary h-11 text-base placeholder:text-[#9CA3AF]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="name" rules={{
      required: isZh ? "請輸入聯絡人姓名" : "Name is required",
      minLength: {
        value: 2,
        message: isZh ? "最少輸入 2 個字元" : "Minimum 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[0.75rem] font-mono font-medium tracking-[0.15em] uppercase text-foreground">
              {isZh ? "聯絡人姓名" : "Contact Person"}
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Smith" {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary h-11 text-base placeholder:text-[#9CA3AF]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="role" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[0.75rem] font-mono font-medium tracking-[0.15em] uppercase text-foreground">
              {isZh ? "職位名稱" : "Job Title"}
            </FormLabel>
            <FormControl>
              <Input placeholder={isZh ? "產品經理" : "Product Manager"} {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary h-11 text-base placeholder:text-[#9CA3AF]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: isZh ? "請輸入商業電郵" : "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: isZh ? "請輸入有效的電郵地址" : "Enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[0.75rem] font-mono font-medium tracking-[0.15em] uppercase text-foreground">
              {isZh ? "商業電郵" : "Business Email"}
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@company.com" {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary h-11 text-base placeholder:text-[#9CA3AF]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[0.75rem] font-mono font-medium tracking-[0.15em] uppercase text-foreground">
              {isZh ? "電話號碼" : "Phone Number"}
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 555-000-0000" {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary h-11 text-base placeholder:text-[#9CA3AF]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="briefing_topic" rules={{
      required: isZh ? "請描述您的項目需求" : "Please describe your project"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[0.75rem] font-mono font-medium tracking-[0.15em] uppercase text-foreground">
              {isZh ? "項目需求詳情" : "Project Requirements"}
            </FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder={isZh ? "請描述您的產品類型、預計產量或特定製造需求..." : "Describe your product, volume, or manufacturing needs..."} {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary resize-none text-base placeholder:text-[#9CA3AF]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[0.75rem] font-mono font-medium tracking-[0.15em] uppercase text-foreground">
              {isZh ? "預計推出日期" : "Target Launch Date"}
            </FormLabel>
            <FormControl>
              <Input type="date" {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary h-11 text-base placeholder:text-[#9CA3AF]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border border-destructive/40 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} size="lg" className="px-8 py-6 h-auto font-semibold tracking-tight bg-primary text-primary-foreground hover:bg-primary/90">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              {isZh ? "傳送中..." : "Submitting..."}
            </> : isZh ? "提交查詢" : "Submit Inquiry"}
        </Button>
        <p className="mt-5 text-[0.85rem] text-muted-foreground tracking-[0.02em] leading-relaxed flex items-start gap-2">
          <Lock className="size-3.5 mt-0.5 shrink-0" />
          <span>
            {isZh ? "所有資料均經安全加密傳輸。我們將於一個工作天內回覆所有生產查詢。" : "All data is transmitted securely. We respond to all manufacturing inquiries within one business day."}
          </span>
        </p>
      </div>
    </Form>;
}
export default function Contact() {
  const [isZh, setIsZh] = useState(false);
  useEffect(() => {
    const checkZh = () => {
      setIsZh(localStorage.getItem("elite-language") === "zh-Hant");
    };
    checkZh();
    window.addEventListener("storage", checkZh);
    return () => window.removeEventListener("storage", checkZh);
  }, []);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const deliverables = isZh ? deliverablesZh : deliverablesEn;
  return <section data-section-id="15863" id="start-your-project" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16">
      <div className="max-w-[1280px] mx-auto">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-8">
            <div className="border border-border bg-card p-8 md:p-12">
              <div className="w-16 h-px bg-primary mb-6" />
              <p className="text-[0.75rem] font-mono font-medium tracking-[0.15em] uppercase text-muted-foreground mb-6">
                {isZh ? "製造業務查詢" : "Manufacturing Inquiry"}
              </p>
              <h2 className="font-default font-semibold text-4xl md:text-5xl lg:text-[3.25rem] leading-[1.1] tracking-tighter text-foreground mb-6">
                {isZh ? <>
                    開啟您的<em className="text-primary not-italic">生產製造</em>合作之旅。
                  </> : <>
                    Start your <em className="text-primary not-italic">production</em> journey.
                  </>}
              </h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-10 max-w-2xl">
                {isZh ? "攜手 Elite Group Corporation Limited，獲得一站式端到端 OEM/ODM 製造服務。提交您的項目詳情，即可獲取完整製造能力簡報，並與我們的專業工程團隊進行一對一諮詢。" : "Partner with Elite Group Corporation Limited for end-to-end OEM/ODM manufacturing. Submit your project details to receive a comprehensive capability brief and a consultation with our engineering team."}
              </p>

              <FormProvider formKey="start_project_form_contact" formId="start_project_form_contact" sectionName="start-your-project" defaultValues={{
              name: "",
              email: "",
              organization: "",
              role: "",
              phone: "",
              briefing_topic: "",
              preferred_date: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              organization: {
                required: true
              },
              phone: {
                pattern: "phone"
              },
              briefing_topic: {
                required: true
              }
            }} submitText={isZh ? "提交查詢" : "Submit Inquiry"} successMessage={isZh ? "已收到您的查詢。我們的團隊正在評估您的需求。" : "Inquiry received. Our team is reviewing your requirements."} errorMessage={isZh ? "未能傳送您的查詢，請稍後再試或直接與我們聯絡。" : "We could not transmit your inquiry. Please try again or contact us directly."}>
                <BriefingForm isZh={isZh} />
              </FormProvider>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="border border-border bg-card p-8 md:p-10 lg:sticky lg:top-8">
              <div className="w-16 h-px bg-primary mb-6" />
              <p className="text-[0.75rem] font-mono font-medium tracking-[0.15em] uppercase text-muted-foreground mb-6">
                {isZh ? "合作交付內容" : "Deliverables"}
              </p>
              <h3 className="font-default font-semibold text-xl md:text-2xl leading-snug tracking-tighter text-foreground mb-6">
                {isZh ? "您將獲得的支援" : "What you'll receive"}
              </h3>
              <ul className="space-y-4 mb-10">
                {deliverables.map((item, i) => <li data-index={i} key={i} className="flex items-start gap-3 text-foreground">
                    <Check className="size-4 mt-1 shrink-0 text-primary" />
                    <span className="text-base leading-relaxed">{item}</span>
                  </li>)}
              </ul>

              <div className="border-t border-border pt-6">
                <p className="text-[0.75rem] font-mono font-medium tracking-[0.15em] uppercase text-muted-foreground mb-5">
                  {isZh ? "直接聯絡" : "Direct Contact"}
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Phone className="size-4 mt-1 shrink-0 text-muted-foreground" />
                    <div>
                      <p className="text-[0.85rem] text-muted-foreground tracking-[0.02em] mb-1">
                        {isZh ? "生產專線" : "Production Line"}
                      </p>
                      <p className="font-mono text-sm text-foreground">+852 5626 2785</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="size-4 mt-1 shrink-0 text-muted-foreground" />
                    <div>
                      <p className="text-[0.85rem] text-muted-foreground tracking-[0.02em] mb-1">
                        {isZh ? "查詢電郵" : "Inquiries"}
                      </p>
                      <p className="font-mono text-sm text-foreground break-all">km.chan@elite-corp.com.hk</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Lock className="size-4 mt-1 shrink-0 text-muted-foreground" />
                    <div>
                      <p className="text-[0.85rem] text-muted-foreground tracking-[0.02em] mb-1">
                        {isZh ? "保密承諾" : "Confidentiality"}
                      </p>
                      <p className="text-sm text-foreground leading-relaxed">
                        {isZh ? "受保密協議（NDA）嚴格保護" : "NDA-protected communication"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}